import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  VersionHistory,
  VersionComparison,
  RollbackForm,
  RollbackResult,
  APIResponse,
  UseVersionHistoryResult,
} from '../types/mediaRecord';

// API service functions
const versionApi = {
  getHistory: async (
    mediaId: string,
    tenantId: string,
    params: {
      limit?: number;
      offset?: number;
      includeDeleted?: boolean;
    } = {}
  ): Promise<VersionHistory> => {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });

    const response = await fetch(`/api/media/${mediaId}/versions?${searchParams}`, {
      headers: {
        'X-Tenant-ID': tenantId,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch version history: ${response.statusText}`);
    }

    const result: APIResponse<VersionHistory> = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error(result.message || 'Failed to fetch version history');
    }

    return result.data;
  },

  rollback: async (
    mediaId: string,
    tenantId: string,
    rollbackData: RollbackForm
  ): Promise<RollbackResult> => {
    const response = await fetch(`/api/media/${mediaId}/rollback`, {
      method: 'POST',
      headers: {
        'X-Tenant-ID': tenantId,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(rollbackData),
    });

    if (!response.ok) {
      throw new Error(`Failed to rollback version: ${response.statusText}`);
    }

    const result: APIResponse<RollbackResult> = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error(result.message || 'Failed to rollback version');
    }

    return result.data;
  },

  compare: async (
    mediaId: string,
    tenantId: string,
    versionA: string,
    versionB: string
  ): Promise<VersionComparison> => {
    const response = await fetch(`/api/media/${mediaId}/versions/compare`, {
      method: 'POST',
      headers: {
        'X-Tenant-ID': tenantId,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        version_a: versionA,
        version_b: versionB,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to compare versions: ${response.statusText}`);
    }

    const result: APIResponse<VersionComparison> = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error(result.message || 'Failed to compare versions');
    }

    return result.data;
  },
};

// Helper function to get tenant ID
const getTenantId = (): string => {
  return localStorage.getItem('tenantId') || process.env.NEXT_PUBLIC_DEFAULT_TENANT_ID || '';
};

/**
 * Custom hook for managing version history
 */
export const useVersionHistory = (
  mediaId: string,
  options: {
    limit?: number;
    includeDeleted?: boolean;
    autoRefresh?: boolean;
  } = {}
): UseVersionHistoryResult => {
  const queryClient = useQueryClient();
  const tenantId = getTenantId();

  const {
    limit = 50,
    includeDeleted = false,
    autoRefresh = false,
  } = options;

  // Query for version history
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['versionHistory', mediaId, tenantId, limit, includeDeleted],
    queryFn: () => versionApi.getHistory(mediaId, tenantId, {
      limit,
      includeDeleted,
    }),
    enabled: !!mediaId && !!tenantId,
    staleTime: 60000, // 1 minute
    gcTime: 300000, // 5 minutes
    refetchInterval: autoRefresh ? 30000 : false, // Auto-refresh every 30 seconds if enabled
    retry: (failureCount, error) => {
      if (error.message.includes('404') || error.message.includes('401')) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Mutation for rollback
  const rollbackMutation = useMutation({
    mutationFn: (rollbackData: RollbackForm) =>
      versionApi.rollback(mediaId, tenantId, rollbackData),
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['versionHistory', mediaId, tenantId] });
      await queryClient.cancelQueries({ queryKey: ['mediaRecord', mediaId, tenantId] });
    },
    onSuccess: (result) => {
      // Invalidate and refetch version history and media record
      queryClient.invalidateQueries({ queryKey: ['versionHistory', mediaId] });
      queryClient.invalidateQueries({ queryKey: ['mediaRecord', mediaId] });
      queryClient.invalidateQueries({ queryKey: ['mediaAnalytics', mediaId] });
      
      toast.success(
        `Successfully rolled back to version ${result.version}${
          result.backup_version ? ` (backup created: ${result.backup_version})` : ''
        }`
      );
    },
    onError: (error) => {
      toast.error(`Failed to rollback: ${error.message}`);
    },
  });

  // Mutation for version comparison
  const compareMutation = useMutation({
    mutationFn: ({ versionA, versionB }: { versionA: string; versionB: string }) =>
      versionApi.compare(mediaId, tenantId, versionA, versionB),
    onError: (error) => {
      toast.error(`Failed to compare versions: ${error.message}`);
    },
  });

  const rollback = async (rollbackData: RollbackForm): Promise<RollbackResult> => {
    return rollbackMutation.mutateAsync(rollbackData);
  };

  const compare = async (versionA: string, versionB: string): Promise<VersionComparison> => {
    return compareMutation.mutateAsync({ versionA, versionB });
  };

  return {
    data,
    isLoading,
    error: error as Error | null,
    rollback,
    compare,
    isRollingBack: rollbackMutation.isPending,
  };
};

/**
 * Hook for version comparison operations
 */
export const useVersionComparison = () => {
  const tenantId = getTenantId();

  const compareMutation = useMutation({
    mutationFn: ({ 
      mediaId, 
      versionA, 
      versionB 
    }: { 
      mediaId: string; 
      versionA: string; 
      versionB: string;
    }) => versionApi.compare(mediaId, tenantId, versionA, versionB),
    onError: (error) => {
      toast.error(`Failed to compare versions: ${error.message}`);
    },
  });

  const compareVersions = async (
    mediaId: string,
    versionA: string,
    versionB: string
  ): Promise<VersionComparison> => {
    return compareMutation.mutateAsync({ mediaId, versionA, versionB });
  };

  return {
    compareVersions,
    isComparing: compareMutation.isPending,
    comparisonResult: compareMutation.data,
    comparisonError: compareMutation.error,
  };
};

/**
 * Hook for version-specific operations
 */
export const useVersionOperations = (mediaId: string) => {
  const queryClient = useQueryClient();
  const tenantId = getTenantId();

  // Create a new version (typically after media update)
  const createVersionMutation = useMutation({
    mutationFn: (versionData: {
      change_type: string;
      description: string;
      changes?: any[];
    }) => {
      // This would call an API endpoint to create a version
      // Implementation depends on your backend API structure
      return Promise.resolve(); // Placeholder
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versionHistory', mediaId] });
      toast.success('Version created successfully');
    },
    onError: (error) => {
      toast.error(`Failed to create version: ${error.message}`);
    },
  });

  // Delete a version
  const deleteVersionMutation = useMutation({
    mutationFn: (versionId: string) => {
      // This would call an API endpoint to delete a version
      // Implementation depends on your backend API structure
      return Promise.resolve(); // Placeholder
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versionHistory', mediaId] });
      toast.success('Version deleted successfully');
    },
    onError: (error) => {
      toast.error(`Failed to delete version: ${error.message}`);
    },
  });

  // Restore a deleted version
  const restoreVersionMutation = useMutation({
    mutationFn: (versionId: string) => {
      // This would call an API endpoint to restore a version
      // Implementation depends on your backend API structure
      return Promise.resolve(); // Placeholder
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['versionHistory', mediaId] });
      toast.success('Version restored successfully');
    },
    onError: (error) => {
      toast.error(`Failed to restore version: ${error.message}`);
    },
  });

  return {
    createVersion: createVersionMutation.mutate,
    deleteVersion: deleteVersionMutation.mutate,
    restoreVersion: restoreVersionMutation.mutate,
    isCreating: createVersionMutation.isPending,
    isDeleting: deleteVersionMutation.isPending,
    isRestoring: restoreVersionMutation.isPending,
  };
};

/**
 * Hook for prefetching version data
 */
export const usePrefetchVersionHistory = () => {
  const queryClient = useQueryClient();
  const tenantId = getTenantId();

  const prefetchVersionHistory = (mediaId: string, limit: number = 50) => {
    queryClient.prefetchQuery({
      queryKey: ['versionHistory', mediaId, tenantId, limit, false],
      queryFn: () => versionApi.getHistory(mediaId, tenantId, { limit }),
      staleTime: 60000,
    });
  };

  return { prefetchVersionHistory };
};

/**
 * Hook for pagination support in version history
 */
export const useVersionHistoryPagination = (
  mediaId: string,
  pageSize: number = 20
) => {
  const queryClient = useQueryClient();
  const tenantId = getTenantId();

  const getVersionPage = async (page: number) => {
    const offset = page * pageSize;
    
    return queryClient.fetchQuery({
      queryKey: ['versionHistory', mediaId, tenantId, pageSize, false, offset],
      queryFn: () => versionApi.getHistory(mediaId, tenantId, {
        limit: pageSize,
        offset,
      }),
      staleTime: 60000,
    });
  };

  const prefetchNextPage = (currentPage: number) => {
    const nextOffset = (currentPage + 1) * pageSize;
    
    queryClient.prefetchQuery({
      queryKey: ['versionHistory', mediaId, tenantId, pageSize, false, nextOffset],
      queryFn: () => versionApi.getHistory(mediaId, tenantId, {
        limit: pageSize,
        offset: nextOffset,
      }),
      staleTime: 60000,
    });
  };

  return {
    getVersionPage,
    prefetchNextPage,
  };
};